import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Eye, EyeOff, KeyRound, Loader2, UserRound, Users } from 'lucide-react';
import { vendorApi } from '../../services/api';
import Sidebar from '../../components/Sidebar';
import TopBar from '../../components/TopBar';
import StatusBadge from '../../components/StatusBadge';
import { formatDate, isBlankDate } from '../../utils/format';

/** The same rules BC enforces (FSCSPO Vendor Cred Mgt.IsStrongPassword) */
const passwordRules = [
  { test: (p: string) => p.length >= 10, label: 'Minst 10 tecken' },
  { test: (p: string) => /[A-Za-zÅÄÖåäöÉéÜü]/.test(p), label: 'Minst en bokstav' },
  { test: (p: string) => /\d/.test(p), label: 'Minst en siffra' },
];

const refusalText: Record<string, string> = {
  invalid: 'Nuvarande lösenord stämmer inte.',
  locked: 'Kontot är tillfälligt låst efter för många felaktiga försök. Försök igen om en stund.',
  weak: 'Det nya lösenordet uppfyller inte kraven.',
  same: 'Det nya lösenordet måste skilja sig från det nuvarande.',
  BC_SERVICE_MISSING: 'Lösenordsbyte är inte tillgängligt än – Business Central behöver uppdateras. Kontakta din åkericentral.',
};

function PasswordField({
  id, label, value, onChange, autoComplete, describedBy,
}: { id: string; label: string; value: string; onChange: (v: string) => void; autoComplete: string; describedBy?: string }) {
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="relative mt-1.5">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          aria-describedby={describedBy}
          required
          maxLength={128}
          className="w-full rounded-full border border-gray-300 py-2.5 pl-4 pr-11 focus:border-transparent focus:ring-2 focus:ring-primary"
        />
        <button
          type="button"
          onClick={() => setVisible(!visible)}
          aria-label={visible ? 'Dölj lösenord' : 'Visa lösenord'}
          className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-ink"
        >
          {visible ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
        </button>
      </div>
    </div>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const rulesMet = passwordRules.every((r) => r.test(next));
  const matches = next.length > 0 && next === repeat;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setDone(false);
    if (!rulesMet) return setError(refusalText.weak);
    if (!matches) return setError('Lösenorden är inte lika.');
    if (next === current) return setError(refusalText.same);
    setBusy(true);
    try {
      await vendorApi.changePassword(current, next);
      setDone(true);
      setCurrent('');
      setNext('');
      setRepeat('');
    } catch (err: any) {
      const code = err?.response?.data?.code;
      setError(refusalText[code] || 'Lösenordet kunde inte bytas. Försök igen om en stund.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="password-heading" className="card p-6 sm:p-8">
      <h2 id="password-heading" className="flex items-center gap-2 text-[15px] font-semibold text-ink">
        <KeyRound className="h-4 w-4 text-blue-600" aria-hidden />
        Byt lösenord
      </h2>
      <p className="mt-1 text-sm text-gray-600">Du är kvar inloggad här. Andra inloggningar med ditt konto avslutas.</p>

      <form onSubmit={submit} className="mt-6 max-w-md space-y-4" noValidate>
        <PasswordField id="current-password" label="Nuvarande lösenord" value={current} onChange={setCurrent} autoComplete="current-password" />
        <PasswordField id="new-password" label="Nytt lösenord" value={next} onChange={setNext} autoComplete="new-password" describedBy="password-rules" />
        <ul id="password-rules" className="space-y-1 text-xs">
          {passwordRules.map((r) => {
            const met = r.test(next);
            return (
              <li key={r.label} className={met ? 'text-emerald-700' : 'text-gray-500'}>
                <span aria-hidden>{met ? '✓' : '·'}</span> {r.label}
                <span className="sr-only">{met ? ' – uppfyllt' : ' – ej uppfyllt'}</span>
              </li>
            );
          })}
        </ul>
        <PasswordField id="repeat-password" label="Upprepa nytt lösenord" value={repeat} onChange={setRepeat} autoComplete="new-password" />
        {repeat && !matches && <p className="text-xs text-red-700">Lösenorden är inte lika.</p>}

        {error && <p role="alert" className="border-l-2 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>}
        {done && <p role="status" className="border-l-2 border-emerald-500 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">Lösenordet är bytt.</p>}

        <button
          type="submit"
          disabled={busy || !current || !rulesMet || !matches}
          className="btn-primary"
        >
          {busy ? 'Byter lösenord…' : 'Byt lösenord'}
          <span className="btn-primary-icon">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <KeyRound className="h-4 w-4" aria-hidden />}
          </span>
        </button>
      </form>
    </section>
  );
}

export default function Settings() {
  const navigate = useNavigate();

  const { data: vendorData, error } = useQuery({
    queryKey: ['vendor', 'me'],
    queryFn: async () => (await vendorApi.me()).data,
  });

  const { data: usersData, isLoading: usersLoading, isError: usersError } = useQuery({
    queryKey: ['vendor', 'users'],
    queryFn: async () => (await vendorApi.getUsers()).data,
  });

  useEffect(() => {
    if (error) navigate('/login');
  }, [error, navigate]);

  const vendor = vendorData?.vendor;
  const users: any[] = usersData?.users || [];
  // The signed-in login (its own name and e-mail); vendor.email is the company's e-mail, not the user's
  const me = users.find((u) => u.isMe);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar logoUrl={vendorData?.tenant?.logoUrl} companyName={vendorData?.tenant?.companyName} />

      <div className="flex flex-1 flex-col">
        <TopBar userName={vendor?.fullName || vendor?.username} />

        <main className="mx-auto w-full max-w-[1400px] flex-1 px-6 py-8 lg:px-10 lg:py-10">
          <h1 className="font-display text-[1.35rem] leading-snug text-ink text-balance md:text-[1.6rem]">Inställningar</h1>
          <p className="mt-1 text-gray-600">Ditt konto och vilka som har inloggning för ert företag</p>

          <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
            <div className="grid gap-6 self-start">
              <section aria-labelledby="account-heading" className="card p-6 sm:p-8">
                <h2 id="account-heading" className="flex items-center gap-2 text-[15px] font-semibold text-ink">
                  <UserRound className="h-4 w-4 text-blue-600" aria-hidden />
                  Ditt konto
                </h2>
                <dl className="mt-5 grid gap-4 text-sm sm:grid-cols-2">
                  {[
                    ['Namn', me?.fullName || (usersLoading ? '…' : '')],
                    ['Användarnamn', me?.userName || vendor?.username],
                    ['E-post för inloggning', me?.email || (usersLoading ? '…' : '')],
                    ['Leverantörsnr', vendor?.vendorNo],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-xs text-gray-500">{label}</dt>
                      <dd className="mt-0.5 break-words font-medium text-ink">{value || '–'}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-5 text-xs text-gray-500">Namn och e-post ändras av din åkericentral.</p>
              </section>

              <ChangePassword />
            </div>

            <section aria-labelledby="users-heading" className="card self-start">
              <div className="p-6 pb-4 sm:px-8">
                <h2 id="users-heading" className="flex items-center gap-2 text-[15px] font-semibold text-ink">
                  <Users className="h-4 w-4 text-blue-600" aria-hidden />
                  Användare för {vendorData?.vendor?.vendorNo ? `leverantör ${vendorData.vendor.vendorNo}` : 'ert företag'}
                </h2>
                <p className="mt-1 text-sm text-gray-600">Nya användare och spärrar hanteras av din åkericentral.</p>
              </div>
              {usersLoading ? (
                <p className="px-8 pb-8 text-sm text-gray-500">Hämtar användare…</p>
              ) : usersError ? (
                <p role="alert" className="px-8 pb-8 text-sm text-red-800">Användarna kunde inte hämtas.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {['Namn', 'E-post', 'Senast inloggad', 'Status'].map((h) => (
                          <th key={h} scope="col" className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 sm:first:pl-8">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {users.map((u) => (
                        <tr key={u.userName}>
                          <td className="px-5 py-3.5 text-sm sm:pl-8">
                            <span className="block font-medium text-ink">
                              {u.fullName || u.userName}
                              {u.isMe && <span className="ml-2 text-xs font-normal text-gray-500">(du)</span>}
                            </span>
                            <span className="block text-xs text-gray-500">{u.userName}</span>
                          </td>
                          <td className="px-5 py-3.5 text-sm text-gray-700">{u.email || '–'}</td>
                          <td className="whitespace-nowrap px-5 py-3.5 text-sm text-gray-700">
                            {isBlankDate(u.lastLogin) ? 'Aldrig' : formatDate(u.lastLogin)}
                          </td>
                          <td className="whitespace-nowrap px-5 py-3.5 text-sm">
                            <StatusBadge tone={u.active ? 'green' : 'gray'}>{u.active ? 'Aktiv' : 'Spärrad'}</StatusBadge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}
